import React, { useState, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { updateIntake, updateTodaysIntake, setIntakeHistory } from "../redux/actions/nutritionActions";
import { updateTodaysIntakeDemo, updateIntakeDemo, setIntakeHistoryDemo } from "../redux/actions/demoActions";
import { setIntakeHeight } from "../redux/actions/heightActions";
import "../styles/intake.scss";
import IntakeInput from "./sub_components/IntakeInput";
import OrangeSeperator from "../components/OrangeSeperator";
import { getFormattedDate } from "../utils/getFormattedDate";
import { useWindowWidth } from "../hooks/useWindowWidth";

const Intake = () => {
  const todays_intake = useSelector((state) => state.nutritionReducer.todays_intake);
  const todays_intake_demo = useSelector((state) => state.demoReducer.todays_intake_demo);
  const intake_history = useSelector((state) => state.nutritionReducer.intake_history);
  const intake_history_demo = useSelector((state) => state.demoReducer.intake_history_demo);
  const hero_height = useSelector((state) => state.heightReducer.hero_height);
  const modifyIntake_height = useSelector((state) => state.heightReducer.modifyIntake_height);
  const intake_height = useSelector((state) => state.heightReducer.intake_height);
  const demo = useSelector((state) => state.demoReducer.demo);
  const activeList = demo ? todays_intake_demo : todays_intake;
  const dispatch = useDispatch();
  const intakeRef = useRef(null);
  const [nutrients] = useState(["calories", "fat", "protein", "carbs", "amount"]);
  const [calculatedNutrition, setCalculatedNutrition] = useState([]);
  const [errorMessage, setErrorMessage] = useState(false);
  const [caloriesGoal, setCaloriesGoal] = useState(2500);
  const [caloriesGoalDemo, setCaloriesGoalDemo] = useState(2500);
  const [fatGoal, setFatGoal] = useState(60);
  const [fatGoalDemo, setFatGoalDemo] = useState(60);
  const [proteinGoal, setProteinGoal] = useState(80);
  const [proteinGoalDemo, setProteinGoalDemo] = useState(80);
  const [carbsGoal, setCarbsGoal] = useState(100);
  const [carbsGoalDemo, setCarbsGoalDemo] = useState(100);
  const [amountGoal, setAmountGoal] = useState(600);
  const [amountGoalDemo, setAmountGoalDemo] = useState(600);
  const windowWidth = useWindowWidth();

  // Gets intake height and sends it to the redux store
  useEffect(() => {
    dispatch(setIntakeHeight(intakeRef.current != null && intakeRef.current.getBoundingClientRect().height));
  }, [dispatch, intake_history, intake_history_demo, windowWidth]);

  // Sums up each nutrient from the currently active list
  useEffect(() => {
    setCalculatedNutrition(
      nutrients.map((nutrient) => activeList.map((item) => parseInt(item[nutrient])).reduce((a, b) => a + b, 0))
    );
  }, [activeList, nutrients]);

  // Sends the calculated nutrition to the redux store
  useEffect(() => {
    const intakeInput = calculatedNutrition.reduce((obj, item, index) => {
      obj[nutrients[index]] = item;
      return obj;
    }, {});
    dispatch(activeList === todays_intake ? updateIntake(intakeInput) : updateIntakeDemo(intakeInput));
  }, [dispatch, activeList, nutrients, calculatedNutrition, todays_intake]);

  // Handles localStorage saving
  useEffect(() => {
    const ls = JSON.parse(localStorage.getItem("intake"));
    if (ls) {
      setCaloriesGoal(ls.caloriesGoal ?? 2500);
      setFatGoal(ls.fatGoal ?? 60);
      setProteinGoal(ls.proteinGoal ?? 50);
      setCarbsGoal(ls.carbsGoal ?? 100);
      setAmountGoal(ls.amountGoal ?? 600);
    }
  }, []);

  // Handles localStorage saving
  useEffect(() => {
    localStorage.setItem(
      "intake",
      JSON.stringify({
        caloriesGoal,
        fatGoal,
        proteinGoal,
        carbsGoal,
        amountGoal,
      })
    );
  }, [caloriesGoal, fatGoal, proteinGoal, carbsGoal, amountGoal]);

  // Handles intake submit
  const handleSubmit = () => {
    setErrorMessage();
    if (calculatedNutrition.every((item) => item === 0)) {
      setErrorMessage(true);
    } else {
      setErrorMessage(false);
      let intakeInput = calculatedNutrition.reduce((obj, item, index) => {
        obj[nutrients[index]] = item;
        return obj;
      }, {});
      intakeInput.date = getFormattedDate(new Date());
      intakeInput.id = Math.random();
      if (activeList === todays_intake) {
        dispatch(setIntakeHistory([...intake_history, intakeInput]));
        dispatch(updateTodaysIntake([]));
      } else {
        dispatch(setIntakeHistoryDemo([...intake_history_demo, intakeInput]));
        dispatch(updateTodaysIntakeDemo([]));
      }
    }
  };

  // Handles intake input change
  const handleChange = (value, setNutritionGoal, setNutritionGoalDemo) => {
    if (demo) {
      setNutritionGoalDemo(value);
    } else {
      setNutritionGoal(value);
    }
  };

  return (
    <div className="intake" ref={intakeRef}>
      <h3 className="intake__title">
        <span className="intake__title-rotated">Intake</span>
      </h3>
      {windowWidth <= 620 && <OrangeSeperator />}
      <div className="intake__content">
        <div className="intake__description">
          Here you can see your current nutrition status for today, by clicking the SUBMIT button you submit the current
          nutrition data to the{" "}
          <span
            className="intake__highlighted"
            onClick={() => window.scrollTo(0, hero_height + modifyIntake_height + intake_height)}
          >
            “INTAKE HISTORY” section
          </span>
          . When the data is submitted every item from the{" "}
          <span className="intake__highlighted" onClick={() => window.scrollTo(0, hero_height)}>
            “TODAY’S INTAKE” section
          </span>{" "}
          deletes.
        </div>
        <div className="intake__nutrition">
          <ul className="intake__nutrition-list">
            <IntakeInput
              name="calories"
              unit="cal"
              goal={demo ? caloriesGoalDemo : caloriesGoal}
              onChange={(value) => handleChange(value, setCaloriesGoal, setCaloriesGoalDemo)}
            />
            <IntakeInput
              name="fat"
              unit="g"
              goal={demo ? fatGoalDemo : fatGoal}
              onChange={(value) => handleChange(value, setFatGoal, setFatGoalDemo)}
            />
            <IntakeInput
              name="protein"
              unit="g"
              goal={demo ? proteinGoalDemo : proteinGoal}
              onChange={(value) => handleChange(value, setProteinGoal, setProteinGoalDemo)}
            />
            <IntakeInput
              name="carbs"
              unit="g"
              goal={demo ? carbsGoalDemo : carbsGoal}
              onChange={(value) => handleChange(value, setCarbsGoal, setCarbsGoalDemo)}
            />
            <IntakeInput
              name="amount"
              unit="g"
              goal={demo ? amountGoalDemo : amountGoal}
              onChange={(value) => handleChange(value, setAmountGoal, setAmountGoalDemo)}
            />
          </ul>
          {errorMessage ? <p className="intake__nutrient-error">You cannot submit an empty intake</p> : ""}
          <button className="intake__nutrient-submit" onClick={handleSubmit}>
            Submit
          </button>
        </div>
      </div>
    </div>
  );
};

export default Intake;
